<script lang="ts">
  type FAQItem = {
    question: string;
    answer: string;
  };

  const faqs: FAQItem[] = [
    {
      question: 'Is my financial data really private?',
      answer: 'Absolutely. All data stays in your browser using local storage (IndexedDB). We don\'t have servers, cloud storage, or any telemetry. If you use cloud AI providers (Claude/GPT), only transaction descriptions are sent for categorization, never amounts or personal details.',
    },
    {
      question: 'What banks are supported?',
      answer: 'We support CSV imports from Chase, American Express, US Bank, SoFi, BMO, and more. The app uses smart auto-detection to recognize formats. Need support for another bank? Leave a support ticket with a sample CSV (remove sensitive data) and we\'ll add it!',
    },
    {
      question: 'Why manual CSV imports instead of automatic syncing?',
      answer: 'Keep intentionally avoids third-party bank connectors like Plaid. CSV imports take seconds and eliminate account linking, re-authentication issues, and external data access. For quarterly or yearly reviews, this approach gives you complete control with zero ongoing maintenance. No broken connections to fix, no third parties accessing your accounts.',
    },
    {
      question: 'How does categorization work?',
      answer: 'Transactions are automatically matched using smart rules and fuzzy name matching (fuse.js). For uncategorized transactions, you can connect your own AI (Anthropic Claude or OpenAI GPT) to bulk categorize them. You control the model and cost. The AI analyzes merchant names to suggest categories. You review and approve suggestions, and can create rules for future transactions. Local LLM support (Ollama) may be added in the future.',
    },
    {
      question: 'Can multiple family members use this?',
      answer: 'Yes! The app automatically detects people from your folder structure (e.g., Transactions/Chase/John/). You can track spending per person, set individual budgets, and see analytics for each family member.',
    },
    {
      question: 'Who is this for?',
      answer: 'Keep is for intentional financial reviews on your schedule (monthly, quarterly, or yearly). It\'s perfect if you want clarity without complexity, care about privacy, and prefer not to pay subscriptions. Keep is not for daily transaction tracking, envelope-based budgeting, or real-time spending alerts. If you want to obsessively check every purchase, YNAB or Mint-style apps are better fits.',
    },
    {
      question: 'What does it cost?',
      answer: 'Free tier: 100% free forever with local browser storage and your own AI API keys (Claude, GPT). Paid tier (future): Optional cloud sync and hosted AI service for convenience. You choose the tradeoff between cost and convenience. No ads, ever.',
    },
    {
      question: 'Is this really free?',
      answer: 'Yes! The core app is 100% free with no forced subscriptions, no ads, and no hidden costs. Use your own AI keys (optional), store data in your browser, and you\'ll never pay us a cent. We may offer optional paid convenience services in the future (cloud sync, hosted AI), but the core features will always be free.',
    },
    {
      question: 'How do I get started?',
      answer: 'Just click "Start Your First Review" and the app loads instantly in your browser. No installation needed! Import your bank CSVs, configure AI if desired, and start reviewing your finances. For offline access, you can install it as a PWA (Progressive Web App) from your browser menu.',
    },
  ];

  let openIndex = $state<number | null>(null);

  function toggle(index: number) {
    openIndex = openIndex === index ? null : index;
  }
</script>

<section class="section faq-section">
  <div class="container">
    <div class="section-header">
      <h2>Frequently Asked Questions</h2>
      <p>Everything you need to know</p>
    </div>
    <div class="faq-list">
      {#each faqs as faq, index}
        <div class="faq-item">
          <button
            class="faq-question"
            class:open={openIndex === index}
            onclick={() => toggle(index)}
          >
            <span>{faq.question}</span>
            <svg
              class="faq-icon"
              class:rotated={openIndex === index}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {#if openIndex === index}
            <div class="faq-answer">
              <p>{faq.answer}</p>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  </div>
</section>

<style>
  .faq-section {
  }

  .section-header {
    text-align: center;
    max-width: 700px;
    margin: 0 auto var(--spacing-2xl);
  }

  .section-header h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--spacing-sm);
  }

  .section-header p {
    font-size: var(--font-size-lg);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .faq-list {
    max-width: 800px;
    margin: 0 auto;
    display: grid;
    gap: var(--spacing-sm);
  }

  .faq-item {
    background: var(--color-background);
    border: 1.5px solid var(--color-border);
    border-radius: var(--radius-md);
    overflow: hidden;
    transition: all var(--transition-fast);
  }

  .faq-item:hover {
    border-color: var(--color-primary);
  }

  .faq-question {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-md) var(--spacing-lg);
    background: none;
    border: none;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    cursor: pointer;
    text-align: left;
    transition: background var(--transition-fast);
  }

  .faq-question:hover {
    background: var(--color-background-alt);
  }

  .faq-question.open {
    background: var(--color-background-alt);
  }

  .faq-icon {
    flex-shrink: 0;
    transition: transform var(--transition-base);
    color: var(--color-primary);
  }

  .faq-icon.rotated {
    transform: rotate(180deg);
  }

  .faq-answer {
    padding: 0 var(--spacing-lg) var(--spacing-md);
    animation: slideDown var(--transition-base);
  }

  .faq-answer p {
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    font-size: var(--font-size-sm);
    margin: 0;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
